/**
 * Anagram SFX module — mp3-backed buffer cache, mirrors the WhatIs pattern.
 * Logical cue methods load mp3s from src/assets/sfx and route through
 * audioBus.sfxGain().
 *
 * Cues:
 *   chime  — round opens
 *   clack  — per-letter on initial jumble (rapid, keyboard-clatter feel)
 *   tick   — countdown last 5 seconds
 *   reveal — per-hint snap / answer-reveal flourish
 *   hint   — distinct chime when a hint letter is revealed
 *   ding   — first correct guess
 */

import chimeUrl from '../assets/sfx/chime.mp3?url';
import dingUrl from '../assets/sfx/ding.mp3?url';
import tickUrl from '../assets/sfx/tick.mp3?url';
import revealUrl from '../assets/sfx/reveal.mp3?url';
import clackUrl from '../assets/sfx/clack.mp3?url';
import hintUrl from '../assets/sfx/hint.mp3?url';

import { audioBus } from './bus.js';

export type SfxName = 'chime' | 'ding' | 'tick' | 'reveal' | 'clack' | 'hint';

const SFX_URL_CHIME = chimeUrl;
const SFX_URL_DING = dingUrl;
const SFX_URL_TICK = tickUrl;
const SFX_URL_REVEAL = revealUrl;
const SFX_URL_CLACK = clackUrl;
const SFX_URL_HINT = hintUrl;

function urlFor(name: SfxName): string {
  if (name === 'chime') return SFX_URL_CHIME;
  if (name === 'ding') return SFX_URL_DING;
  if (name === 'tick') return SFX_URL_TICK;
  if (name === 'reveal') return SFX_URL_REVEAL;
  if (name === 'clack') return SFX_URL_CLACK;
  return SFX_URL_HINT;
}

const buffers = new Map<SfxName, AudioBuffer | null>();
const inflight = new Map<SfxName, Promise<AudioBuffer | null>>();

async function loadBuffer(name: SfxName): Promise<AudioBuffer | null> {
  if (buffers.has(name)) return buffers.get(name) ?? null;
  const existing = inflight.get(name);
  if (existing) return existing;
  const ctx = audioBus.context();
  if (!ctx) return null;
  const url = urlFor(name);
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      buffers.set(name, buf);
      return buf;
    } catch (err) {
      console.warn(`[an-sfx] failed to load ${name} (${url}):`, err);
      buffers.set(name, null);
      return null;
    } finally {
      inflight.delete(name);
    }
  })();
  inflight.set(name, p);
  return p;
}

function playBuffer(ctx: AudioContext, buf: AudioBuffer, name: SfxName): void {
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(audioBus.sfxGain());
    src.start(0);
  } catch (err) {
    console.warn(`[an-sfx] play ${name} failed:`, err);
  }
}

function play(name: SfxName): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  const buf = buffers.get(name);
  if (!buf) {
    void loadBuffer(name).then((b) => {
      if (b && audioBus.isUnlocked()) playBuffer(ctx, b, name);
    });
    return;
  }
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock();
  }
  playBuffer(ctx, buf, name);
}

export const sfx = {
  async preload(): Promise<void> {
    const names: SfxName[] = ['chime', 'ding', 'tick', 'reveal', 'clack', 'hint'];
    await Promise.all(names.map((n) => loadBuffer(n)));
  },
  play,
  chime(): void { play('chime'); },
  ding(): void { play('ding'); },
  tick(): void { play('tick'); },
  reveal(): void { play('reveal'); },
  clack(): void { play('clack'); },
  hint(): void { play('hint'); },
};
